import { getAssignedCenterId, shouldScopeToAssignedCenter } from "./userSession";
import { getStoredContext } from "./contextStorage";

const API_BASE = "https://mis.empowerplus.in";

export const centerApis = {
    trainers: (centerId) => `${API_BASE}/api/trainers/by-center?centerId=${encodeURIComponent(centerId)}`,
    trainersByProject: (centerId, projectId) => `${API_BASE}/api/trainers/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    assessors: (centerId) => `${API_BASE}/api/assessors/by-center?centerId=${encodeURIComponent(centerId)}`,
    assessorsByProject: (centerId, projectId) => `${API_BASE}/api/assessors/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    courses: (centerId) => `${API_BASE}/api/courses/by-center?centerId=${encodeURIComponent(centerId)}`,
    coursesByProject: (centerId, projectId) => `${API_BASE}/api/courses/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    mobilizations: (centerId) => `${API_BASE}/api/mobilizations/by-center?centerId=${encodeURIComponent(centerId)}`,
    mobilizationsByProject: (centerId, projectId) => `${API_BASE}/api/mobilizations/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    candidates: (centerId) => `${API_BASE}/api/candidates/by-center?centerId=${encodeURIComponent(centerId)}`,
    candidatesByProject: (centerId, projectId) => `${API_BASE}/api/candidates/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    attendance: (centerId) => `${API_BASE}/api/attendance/by-center?centerId=${encodeURIComponent(centerId)}`,
    attendanceByProject: (centerId, projectId) => `${API_BASE}/api/attendance/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    otherActivities: (centerId) => `${API_BASE}/api/other-activities/by-center?centerId=${encodeURIComponent(centerId)}`,
    otherActivitiesByProject: (centerId, projectId) => `${API_BASE}/api/other-activities/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    internalAssessments: (centerId) => `${API_BASE}/api/internal-assessments/by-center?centerId=${encodeURIComponent(centerId)}`,
    internalAssessmentsByProject: (centerId, projectId) => `${API_BASE}/api/internal-assessments/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    externalAssessments: (centerId) => `${API_BASE}/api/external-assessments/by-center?centerId=${encodeURIComponent(centerId)}`,
    externalAssessmentsByProject: (centerId, projectId) => `${API_BASE}/api/external-assessments/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    trainingCompletions: (centerId) => `${API_BASE}/api/training-completions/by-center?centerId=${encodeURIComponent(centerId)}`,
    trainingCompletionsByProject: (centerId, projectId) => `${API_BASE}/api/training-completions/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    certificates: (centerId) => `${API_BASE}/api/certificates/by-center?centerId=${encodeURIComponent(centerId)}`,
    certificatesByProject: (centerId, projectId) => `${API_BASE}/api/certificates/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    placements: (centerId) => `${API_BASE}/api/placements/by-center?centerId=${encodeURIComponent(centerId)}`,
    placementsByProject: (centerId, projectId) => `${API_BASE}/api/placements/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    candidateRetentions: (centerId) => `${API_BASE}/api/candidate-retentions/by-center?centerId=${encodeURIComponent(centerId)}`,
    candidateRetentionsByProject: (centerId, projectId) => `${API_BASE}/api/candidate-retentions/by-center-and-project?centerId=${encodeURIComponent(centerId)}&projectId=${encodeURIComponent(projectId)}`,
    userMappings: (centerId) => `${API_BASE}/api/user-mappings/by-center?centerId=${encodeURIComponent(centerId)}`,
    enrollmentVerification: (centerId) => `${API_BASE}/api/enrollment-verification/by-center?centerId=${encodeURIComponent(centerId)}`
};

const CENTER_SCOPED_PATHS = {
    "/api/trainers": "trainers",
    "/api/assessors": "assessors",
    "/api/courses": "courses",
    "/api/mobilizations": "mobilizations",
    "/api/candidates": "candidates",
    "/api/attendance": "attendance",
    "/api/other-activities": "otherActivities",
    "/api/internal-assessments": "internalAssessments",
    "/api/external-assessments": "externalAssessments",
    "/api/training-completions": "trainingCompletions",
    "/api/certificates": "certificates",
    "/api/placements": "placements",
    "/api/candidate-retentions": "candidateRetentions",
    "/api/user-mappings": "userMappings",
    "/api/enrollment-verification": "enrollmentVerification"
};

export const shouldUseCenterScopedApi = () => {
    const contextCenterId = String(getStoredContext()?.centerId || "").trim();

    if (contextCenterId) {
        return true;
    }

    return shouldScopeToAssignedCenter() && Boolean(getAssignedCenterId());
};

const getEffectiveCenterId = () => {
    const contextCenterId = String(getStoredContext()?.centerId || "").trim();

    if (contextCenterId) {
        return contextCenterId;
    }

    return shouldScopeToAssignedCenter() ? getAssignedCenterId() : "";
};

const getScopedApiUrl = (basePath) => {
    const normalizedPath = String(basePath || "").replace(/\/$/, "");
    const centerId = getEffectiveCenterId();
    const apiKey = CENTER_SCOPED_PATHS[normalizedPath];
    const context = getStoredContext();
    const projectId = String(context?.project || "").trim();

    if (!apiKey || !centerId) {
        return `${API_BASE}${normalizedPath}`;
    }

    const projectAwareKey = `${apiKey}ByProject`;

    if (projectId && typeof centerApis[projectAwareKey] === "function") {
        return centerApis[projectAwareKey](centerId, projectId);
    }

    return centerApis[apiKey](centerId);
};

export const getCenterScopedPath = (basePath) => {
    if (!shouldUseCenterScopedApi()) {
        return basePath;
    }

    return getScopedApiUrl(basePath).replace(API_BASE, "");
};

export const getCenterScopedUrl = (baseUrl, basePath) => {
    if (!shouldUseCenterScopedApi()) {
        return `${baseUrl}${basePath}`;
    }

    return getScopedApiUrl(basePath);
};

export const extractCenterIdFromRow = (row) =>
    row?.centerId ||
    row?.center?.centerId ||
    row?.centreId ||
    row?.candidate?.centerId ||
    "";

const normalize = (value) => String(value || "").trim().toLowerCase();
const normalizeProjectToken = (value) =>
    normalize(value).replace(/(\d{4})-(\d{4})/g, (_, start, end) => `${start}-${end.slice(-2)}`);

const expandProjectTokenVariants = (value) => {
    const normalizedValue = normalizeProjectToken(value);

    if (!normalizedValue) {
        return [];
    }

    const variants = new Set([normalizedValue]);
    const pathParts = normalizedValue.split("/").filter(Boolean);

    if (pathParts.length) {
        variants.add(pathParts[pathParts.length - 1]);
    }

    return [...variants];
};

export const extractProjectTokensFromRow = (row) => {
    const values = [
        row?.projectId,
        row?.project?.projectId,
        row?.projectName,
        row?.project?.projectName,
        row?.candidate?.projectId,
        row?.candidate?.projectName,
        row?.batch?.projectId,
        row?.batch?.project?.projectId,
        row?.batch?.projectName,
        row?.batch?.project?.projectName
    ];

    return [...new Set(values.flatMap(expandProjectTokenVariants))];
};

export const matchesCurrentContext = (row) => {
    const context = getStoredContext();
    const selectedProjectTokens = new Set([
        ...expandProjectTokenVariants(context?.project),
        ...expandProjectTokenVariants(context?.projectName)
    ]);

    if (!selectedProjectTokens.size) {
        return true;
    }

    const projectTokens = extractProjectTokensFromRow(row);

    if (!projectTokens.length) {
        return true;
    }

    return (
        projectTokens.some((token) => selectedProjectTokens.has(token))
    );
};

export const filterRowsForCurrentCenter = (rows) => {
    const effectiveCenterId = getEffectiveCenterId();

    if (!effectiveCenterId) {
        return rows;
    }

    return rows.filter((row) => extractCenterIdFromRow(row) === effectiveCenterId);
};

export const filterRowsForCurrentScope = (rows) => {
    return filterRowsForCurrentCenter(rows).filter(matchesCurrentContext);
};
